import { format, parseISO, isValid } from "date-fns";
import { DateTime } from "luxon";
import type {
  CalendarEvent,
  GoogleCalendarEvent,
  MicrosoftCalendarEvent,
} from "../types/calendar.js";

/**
 * Normalize Google Calendar Event to unified format
 */
export function normalizeGoogleEvent(
  event: GoogleCalendarEvent,
  calendarEmail?: string,
): CalendarEvent {
  const isAllDayEvent = Boolean(
    event.start?.date &&
      event.end?.date &&
      !event.start?.dateTime &&
      !event.end?.dateTime,
  );

  // Handle date parsing safely
  const startDate = parseGoogleDateTime(
    event.start?.dateTime,
    event.start?.timeZone,
    event.start?.date,
  );
  const endDate = parseGoogleDateTime(
    event.end?.dateTime,
    event.end?.timeZone,
    event.end?.date,
  );

  // Source must always be the calendar owner (calendarEmail), not the organizer
  // If calendarEmail is not provided, fall back to "unknown" (should not happen in normal flow)
  const source = calendarEmail ? calendarEmail.toLowerCase().trim() : "unknown";
  
  // Organizer email is separate from source (for invited meetings, organizer ≠ calendar owner)
  const organizerEmail = event.organizer?.email || event.creator?.email;
  
  // Create unique ID with provider:calendarId:eventId prefix to prevent collisions
  // Sanitize source to ensure it's safe for use in IDs (replace special chars with underscore)
  const safeCalendarId = source.replace(/[^a-zA-Z0-9@._-]/g, '_');
  const uniqueId = `google:${safeCalendarId}:${event.id}`;
  
  // Validate required fields
  const title = event.summary || "Untitled Event";
  const start = startDate.toISOString();
  const end = endDate.toISOString();
  
  // Ensure all required fields are valid
  if (!uniqueId || !title || !start || !end) {
    console.warn('⚠️ [Calendar Utils] Invalid Google event data:', {
      id: event.id,
      hasTitle: !!title,
      hasStart: !!start,
      hasEnd: !!end,
      source,
    });
  }

  return {
    id: uniqueId,
    title,
    description: event.description,
    start,
    end,
    source, // Calendar owner (the calendar we fetched from)
    organizerEmail, // Event organizer/creator (may differ from source)
    location: event.location,
    attendees: event.attendees?.map((attendee) => attendee.email) || [],
    organizer: event.organizer?.email, // Legacy field
    allDay: isAllDayEvent,
    isCancelled: event.status === "cancelled",
    isOnlineMeeting: !!(event.hangoutLink || event.conferenceData),
    onlineMeetingUrl: event.hangoutLink || event.conferenceData?.entryPoints?.[0]?.uri,
    webLink: undefined,
    htmlLink: event.htmlLink,
    originalStart: event.originalStartTime?.dateTime || event.originalStartTime?.date,
    originalEnd: undefined,
    timeZone: event.start.timeZone,
    recurrenceId: event.recurringEventId,
    seriesMasterId: event.recurringEventId,
    showAs: undefined,
    importance: undefined,
    sensitivity: undefined,
    isDraft: false,
    isOrganizer: event.organizer?.self || false,
    responseRequested: true,
    reminderMinutesBeforeStart: undefined,
    isReminderOn: event.reminders?.useDefault !== false,
    categories: undefined,
    etag: event.etag,
    changeKey: undefined,
    iCalUID: event.iCalUID,
    uid: event.iCalUID,
    created: event.created,
    updated: event.updated,
    lastModified: event.updated,
    transactionId: undefined,
  };
}

/**
 * Normalize Microsoft Calendar Event to unified format
 */
export function normalizeMicrosoftEvent(
  event: MicrosoftCalendarEvent,
  calendarEmail?: string,
): CalendarEvent {
  // Handle date parsing safely
  const startDate = parseMicrosoftDateTime(
    event.start?.dateTime,
    event.start?.timeZone,
  );
  const endDate = parseMicrosoftDateTime(
    event.end?.dateTime,
    event.end?.timeZone,
  );

  // Source must always be the calendar owner (calendarEmail), not the organizer
  // If calendarEmail is not provided, fall back to "unknown" (should not happen in normal flow)
  const source = calendarEmail ? calendarEmail.toLowerCase().trim() : "unknown";
  
  // Organizer email is separate from source (for invited meetings, organizer ≠ calendar owner)
  const organizerEmail = event.organizer?.emailAddress?.address;
  
  // Create unique ID with provider:calendarId:eventId prefix to prevent collisions
  // Sanitize source to ensure it's safe for use in IDs (replace special chars with underscore)
  const safeCalendarId = source.replace(/[^a-zA-Z0-9@._-]/g, '_');
  const uniqueId = `microsoft:${safeCalendarId}:${event.id}`;
  
  // Validate required fields
  const title = event.subject || "Untitled Event";
  const start = startDate.toISOString();
  const end = endDate.toISOString();
  
  // Ensure all required fields are valid
  if (!uniqueId || !title || !start || !end) {
    console.warn('⚠️ [Calendar Utils] Invalid Microsoft event data:', {
      id: event.id,
      hasTitle: !!title,
      hasStart: !!start,
      hasEnd: !!end,
      source,
    });
  }

  return {
    id: uniqueId,
    title,
    description: event.bodyPreview || event.body?.content,
    start,
    end,
    source, // Calendar owner (the calendar we fetched from)
    organizerEmail, // Event organizer (may differ from source)
    location: event.location?.displayName,
    attendees:
      event.attendees?.map((attendee) => attendee.emailAddress?.address).filter((email): email is string => Boolean(email)) || [],
    organizer: event.organizer?.emailAddress?.address,
    allDay: event.isAllDay,
    isCancelled: event.isCancelled,
    isOnlineMeeting: event.isOnlineMeeting,
    onlineMeetingUrl: event.onlineMeetingUrl || undefined,
    webLink: event.webLink,
    htmlLink: undefined,
    originalStart: undefined,
    originalEnd: undefined,
    timeZone: event.start.timeZone,
    recurrenceId: event.occurrenceId || undefined,
    seriesMasterId: event.seriesMasterId,
    showAs: event.showAs,
    importance: event.importance,
    sensitivity: event.sensitivity,
    isDraft: event.isDraft,
    isOrganizer: event.isOrganizer,
    responseRequested: event.responseRequested,
    reminderMinutesBeforeStart: event.reminderMinutesBeforeStart,
    isReminderOn: event.isReminderOn,
    categories: event.categories,
    etag: event["@odata.etag"],
    changeKey: event.changeKey,
    iCalUID: event.iCalUId,
    uid: event.uid,
    created: event.createdDateTime,
    updated: event.lastModifiedDateTime,
    lastModified: event.lastModifiedDateTime,
    transactionId: event.transactionId,
  };
}

/**
 * Safely parse Google date-time string using Luxon (matching wtcwebsite logic)
 */
function parseGoogleDateTime(
  dateTime?: string,
  timeZone?: string,
  date?: string,
): Date {
  // All-day events use date-only fields from Google Calendar API.
  if (!dateTime && date) {
    const effectiveTimeZone = timeZone || DateTime.local().zoneName;
    const allDayDate = DateTime.fromISO(date, { zone: effectiveTimeZone });

    if (allDayDate.isValid) {
      return allDayDate.startOf("day").toLocal().toJSDate();
    }

    console.warn(`Invalid Google all-day date format: ${date}`);
    return new Date(NaN);
  }

  if (!dateTime) {
    console.warn("Google dateTime/date is undefined");
    return new Date(NaN);
  }

  try {
    // Use Luxon to parse with timezone, matching wtcwebsite logic
    const effectiveTimeZone = timeZone || "America/Chicago";
    const luxonDateTime = DateTime.fromISO(dateTime, { zone: effectiveTimeZone });
    
    if (luxonDateTime.isValid) {
      // Convert to local time and then to JavaScript Date
      
      return luxonDateTime.toLocal().toJSDate();
    }

    // Fallback: try parsing as UTC if Luxon parsing fails
    const utcDate = new Date(dateTime);
    if (isValid(utcDate)) {
      
      return utcDate;
    }

    // Last resort: return current date
    console.warn(`Invalid Google date format: ${dateTime}`);
    
    return new Date(NaN);
  } catch (error) {
    console.error(`Error parsing Google date: ${dateTime}`, error);
    
    return new Date(NaN);
  }
}

/**
 * Safely parse Microsoft date-time string using Luxon (matching wtcwebsite logic)
 */
function parseMicrosoftDateTime(dateTime?: string, timeZone?: string): Date {
  if (!dateTime) {
    console.warn('Microsoft dateTime is undefined');
    
    return new Date();
  }

  try {
    // Use Luxon to parse with timezone, matching wtcwebsite logic
    const effectiveTimeZone = timeZone || "America/Chicago";
    const luxonDateTime = DateTime.fromISO(dateTime, { zone: effectiveTimeZone });
    
    if (luxonDateTime.isValid) {
      // Convert to local time and then to JavaScript Date
      
      return luxonDateTime.toLocal().toJSDate();
    }

    // Fallback: try parsing as UTC if Luxon parsing fails
    const utcDate = new Date(dateTime);
    if (isValid(utcDate)) {
      
      return utcDate;
    }

    // Last resort: return current date
    console.warn(`Invalid Microsoft date format: ${dateTime}`);
    
    return new Date();
  } catch (error) {
    console.error(`Error parsing Microsoft date: ${dateTime}`, error);
    
    return new Date();
  }
}

/**
 * Format date safely for display
 */
export function formatEventDate(
  date: string | Date,
  formatString: string = "MMM d, h:mm a",
): string {
  try {
    const dateObj = typeof date === "string" ? new Date(date) : date;

    if (!isValid(dateObj)) {
      console.warn(`Invalid date for formatting: ${date}`);

      return "Invalid Date";
    }

    return format(dateObj, formatString);
  } catch (error) {
    console.error(`Error formatting date: ${date}`, error);

    return "Invalid Date";
  }
}

/**
 * Format time range safely
 */
export function formatTimeRange(
  start: string | Date,
  end: string | Date,
): string {
  try {
    const startDate = typeof start === "string" ? new Date(start) : start;
    const endDate = typeof end === "string" ? new Date(end) : end;

    if (!isValid(startDate) || !isValid(endDate)) {
      return "Invalid Time Range";
    }

    const startFormatted = format(startDate, "h:mm a");
    const endFormatted = format(endDate, "h:mm a");

    return `${startFormatted} - ${endFormatted}`;
  } catch (error) {
    console.error("Error formatting time range:", error);

    return "Invalid Time Range";
  }
}

/**
 * Check if an event is currently happening
 */
export function isEventCurrentlyHappening(event: CalendarEvent): boolean {
  try {
    const now = new Date();
    const start = new Date(event.start);
    const end = new Date(event.end);

    return now >= start && now <= end;
  } catch (error) {
    console.error("Error checking if event is currently happening:", error);

    return false;
  }
}

/**
 * Check if an event is in the future
 */
export function isEventInFuture(event: CalendarEvent): boolean {
  try {
    const now = new Date();
    const start = new Date(event.start);

    return start > now;
  } catch (error) {
    console.error("Error checking if event is in future:", error);

    return false;
  }
}

/**
 * Check if an event is in the past
 */
export function isEventInPast(event: CalendarEvent): boolean {
  try {
    const now = new Date();
    const end = new Date(event.end);

    return end < now;
  } catch (error) {
    console.error("Error checking if event is in past:", error);

    return false;
  }
}

/**
 * Sort events by start time
 */
export function sortEventsByStartTime(
  events: CalendarEvent[],
): CalendarEvent[] {
  return events.sort((a, b) => {
    try {
      const dateA = new Date(a.start);
      const dateB = new Date(b.start);

      if (!isValid(dateA) || !isValid(dateB)) {
        return 0;
      }

      return dateA.getTime() - dateB.getTime();
    } catch (error) {
      console.error("Error sorting events:", error);

      return 0;
    }
  });
}

/**
 * Filter events by date range
 */
export function filterEventsByDateRange(
  events: CalendarEvent[],
  startDate: Date,
  endDate: Date,
): CalendarEvent[] {
  return events.filter((event) => {
    try {
      const eventStart = new Date(event.start);
      const eventEnd = new Date(event.end);

      if (!isValid(eventStart) || !isValid(eventEnd)) {
        return false;
      }

      // Event overlaps with the date range
      return eventStart < endDate && eventEnd > startDate;
    } catch (error) {
      console.error("Error filtering events by date range:", error);

      return false;
    }
  });
}
